import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import {
    Afr, DimensionReference, FactTable, FactTableRow, generateLocalStateFromEventProperties,
    getDimensionForFact, getSelectedAfr, getTable, MatchTableFacts
} from '../../redux/AfrSlice';
import { loadConcept } from '../../redux/ConceptSlice';
import { addColumnFilterAccountWithoutFacts, ColumnFilterAccountWithoutFact, selectColumnFilterAccountsWithoutFacts, setIsDirty } from '../../redux/MiscSlice';
import { DimensionalAccountScope, getAccountFilterForFundGroup, selectScopeNetworks } from '../../redux/NetworkSlice';
import { ActiveTaxonomyVersionInfo, selectCurrentVersion, TaxonomyTypeEnum } from '../../redux/TaxonomyVersionSlice';
import { AdminUserRoleEnum, selectAdminUserRole } from '../../redux/UserSlice';
import { ErrorSummaryAFRSection, getErrorList } from '../../redux/ValidationSlice';
import enums from '../../utils/enums';
import utilityHelper, { AccountRangeFilter } from '../../utils/utilityHelper';
import SelectNewAccountModal from '../layout/Modal/SelectNewAccountModal';

export interface DimensionColumnSpecs {
    axis: string,
    title: string,
    codeLabelMap: any
}

export interface IAccountEditorGridNoSubaccountsOptions {
    gridname: string;
    cellEditHandler: any;
    doneFlag: string;
    noFlag: string;
    fixedRows: boolean;
    firstFieldId: string;
    allowedCombinations: Array<string> | null;
    dimensionColumn: DimensionColumnSpecs | null;
    accountRangeFilter: AccountRangeFilter | null;
    accountGroups: any | null;
    columnFilter: string | null;
}
export const fillFixedRows = function (acctTable: FactTable,
    dimensionColumnAxis: string,
    options: IAccountEditorGridNoSubaccountsOptions,
    tabledata: any[],
    dataColumns: []
) {
    const columnAxis = "FundGroupsAxis"; // Should derive from structure
    var allowedCells: string[] | null = options.allowedCombinations;
    var dimColumnSpecs: DimensionColumnSpecs | null = options.dimensionColumn;
    var rowMapping: any = {};
    var rowDimensions: Array<DimensionReference> = [];
    // If there are row dimensions then we need to extract them
    if (acctTable.structure.rowHierarchy.axes && acctTable.structure.rowHierarchy.axes.length > 0) {
        for (var rowAxis of acctTable.structure.rowHierarchy.axes) {
            if (dimensionColumnAxis && (rowAxis.id === dimensionColumnAxis)) {
                // For now only process one axis - the one designated in the options
                // can be expanded to multiple axes later.
                for (var memConcept of rowAxis.membersByYear) {
                    rowDimensions.push({ axis: rowAxis, member: memConcept, typedContent: null });
                }
            }
        }
    }
    var nextRow: any = {};
    for (var rowConcept of acctTable.structure.rows) {
        var accountLabel = rowConcept.name;
        if (accountLabel.indexOf("Title") >= 0) {
            continue;
        }
        // Now expand the rows on the dimensions (if any)
        if (rowDimensions.length > 0) {
            // Only process the first level for now
            for (var rowDim of rowDimensions) {
                nextRow = {
                    account: accountLabel, accountCode: rowConcept.code, accountId: rowConcept.id
                }
                if (dimensionColumnAxis && options.dimensionColumn && rowDim.member != null
                    && options.dimensionColumn.codeLabelMap.hasOwnProperty(rowDim.member.code)) {
                    var rid = rowConcept.id + "|" + rowDim.member.id;
                    var testid = rowConcept.code + "|" + rowDim.member.id;
                    if (!allowedCells || (allowedCells.indexOf(testid) >= 0)) {
                        var lbl = options.dimensionColumn.codeLabelMap[rowDim.member.code];
                        nextRow[dimensionColumnAxis] = lbl;
                        nextRow["dimensionCode"] = rowDim.member.code;
                        tabledata.push(nextRow);
                        rowMapping[rid] = nextRow;
                    }
                }
            }
        } else {
            nextRow = {
                account: accountLabel, accountCode: rowConcept.code, accountId: rowConcept.id
            }
            tabledata.push(nextRow);
            rowMapping[rowConcept.id] = nextRow;
        }
    }
    if (dimColumnSpecs) {
        // We have a table with a column representing a dimension on the fact. The column values will be the labels of the dimension identified by the dimension
        // column axis. 
        // Currently this is used by the DataElementWorksheet table.
        // Collect all the facts and then reassign to the rows created above
        var allFacts : any[] = [];
        for (var row of acctTable.rows) {
            for (var f of row.facts) {
                allFacts.push(f);
            }
        }
        for (var fTable of allFacts) {
            var factColDim = getDimensionForFact(fTable, dimensionColumnAxis);
            if (!factColDim) {
                // todo throw an error
                continue;
            }
            var accountId = fTable.concept.id + "|" + factColDim.id;
            if (rowMapping.hasOwnProperty(accountId)) {
                var nextRow = rowMapping[accountId];
                var colcode = "amount";
                nextRow[colcode] = fTable.value;
                if (options.dimensionColumn) {
                    var memName = factColDim.code;
                    var lbl = dimColumnSpecs.codeLabelMap[memName];
                    nextRow[dimColumnSpecs.axis] = lbl;
                }
            }
        }
    } else {
        for (var row of acctTable.rows) {
            if (row !== null && row.account !== null) {
                var accountId = row.account.id;
                if (rowMapping.hasOwnProperty(accountId)) {
                    var nextRow = rowMapping[accountId];
                    if (dataColumns.length > 1) {
                        for (var fact of row.facts) {
                            var dimensionForFact = getDimensionForFact(fact, columnAxis);
                            if (dimensionForFact != null) {
                                var colcode = dimensionForFact.id;
                                nextRow[colcode] = fact.value;
                            }
                        }

                    } else {
                        for (var fact of row.facts) {
                            var colcode = "amount";
                            nextRow[colcode] = fact.value;
                        }
                    }
                }
            }
        }
    }
}

function shouldExcludeCode(code: string, filter: AccountRangeFilter): boolean {
    if (code < filter.first) {
        return true;
    }
    if (code > filter.last) {
        return true;
    }
    return false;
}

export function shouldExcludeRow(row: FactTableRow, filter: AccountRangeFilter | null, accountCodeFilter: DimensionalAccountScope | null ): boolean {
    if (!row.account || !row.account.code) {
        return false;
    }
    if (accountCodeFilter && accountCodeFilter.filter
            && accountCodeFilter.filter
                                .split(";")
                                .filter(Boolean) // removes empty strings from trailing semicolon
                                .includes(row.account.code)) {
        // This account should be filtered from this table because its controlled by fund group and appears in the exclusion list
        return true;
    }
    if (!filter) {
        return false;
    }
    return shouldExcludeCode(row.account.code, filter);
}
const AccountEditorGridNoSubAccountBulk = React.memo(
    function AccountEditorGrid(props: {
        tableName: string,
        gridData: any[], gridColumns: any[], gridOptions: any,
        options: IAccountEditorGridNoSubaccountsOptions,
        setHasData: (hasData : boolean) => void
    }) {

        /* ************************************** Redux ********************************************/
        const dispatch = useAppDispatch();
        var tabulatorRef = React.useRef<any>();
        const fieldToEdit = useRef(props.options.firstFieldId)
        const parameters = useLocation();
        const errorList = useAppSelector(getErrorList);
        const columnFilterAccountsWithoutFactsSelector = useAppSelector(selectColumnFilterAccountsWithoutFacts);
        //        const hiddenCols: HiddenColumns[] = useAppSelector(getHiddenColumns);
        //        hiddenCols.map(hc => (hc.tableName === props.tableName) ? hc.fields.map(fld => hiddenColumnMap[fld] = fld) : null );
        const [deleteRequest, setDeleteRequest] = useState<MatchTableFacts | null>(null);
        var versionInfo: ActiveTaxonomyVersionInfo | null = useAppSelector(selectCurrentVersion);
        const [accountCodeFilter, setAccountCodeFilter] = useState<DimensionalAccountScope>({ filter: "", accountMappings: {} });
        const scopeNetworks = useAppSelector(selectScopeNetworks);
        const adminUserRole: AdminUserRoleEnum | null = useAppSelector(selectAdminUserRole);

        /* ************************************** Modal section ********************************************/

        const [modalAccountIsOpen, setModalAccountIsOpen] = useState(false);
        const [dataKey, setDataKey] = useState("");
        const [hiddenColumnMap, setHiddenColumnMap] = useState<any>({});
        const [displayCode, setDisplayCode] = useState<string>("");

        /* *************************************************************************************************/

        const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
        const afrYear = currentAfr ? currentAfr.year : 2019;
        const taxType: TaxonomyTypeEnum = (currentAfr && currentAfr.taxonomyType) ? currentAfr.taxonomyType : TaxonomyTypeEnum.AFR;

        /* *************************************************************************************************/

        const handleCloseAccountModal = function (account: any) {
            if ((typeof account) === "string") {
                setDataKey(account);
                fieldToEdit.current = "FG_001_GeneralFundMember";
            }
            setModalAccountIsOpen(false);
            if (typeof account === "string" && props.options.columnFilter) {
                let newColumnFilterAccountWithoutFact: ColumnFilterAccountWithoutFact = { accountCode: account, columnFilter: props.options.columnFilter ? props.options.columnFilter : "" };
                if (columnFilterAccountsWithoutFactsSelector.some((x: ColumnFilterAccountWithoutFact) => x.accountCode === account && x.columnFilter === newColumnFilterAccountWithoutFact.columnFilter) == false) {
                    dispatch(addColumnFilterAccountWithoutFacts(newColumnFilterAccountWithoutFact));
                }
            }
        }


        function isDoneOrNo() {
            var isDone = true;
            var flag: boolean | null = null;
            var eventNo = generateLocalStateFromEventProperties(currentAfr, [props.options.noFlag]);
            if (props.options.doneFlag === "na") {
                flag = (eventNo[props.options.noFlag] ? true : false) || utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified)
                    || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin);
            } else {
                var eventDone = generateLocalStateFromEventProperties(currentAfr, [props.options.doneFlag]);
                flag = (eventDone[props.options.doneFlag] ? true : false) || (eventNo[props.options.noFlag] ? true : false) || utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified)
                    || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin);
            }


            // NOTE - for new AFRs these flags will be null.
            // Since there may be no assignment true or false, we check for null
            return flag ? flag : false;
        }

        function isDone() {
            var eventDone = generateLocalStateFromEventProperties(currentAfr, [props.options.doneFlag]);
            return eventDone[props.options.doneFlag];
        }
        function isNoData() {
            var isDone = true;
            var flag: boolean | null = null;
            var eventNo = generateLocalStateFromEventProperties(currentAfr, [props.options.noFlag]);
            return eventNo[props.options.noFlag] ? true : false;
        }

        const getEntityCodeFromUrl = function () {
            var returnValue = "";
            if (parameters && parameters.state && parameters.state.entityCode) { return parameters.state.entityCode }
            return returnValue;
        }


        const showErrorIcon = function (cell: any, formatterParams: any, onRendered: any) {
            var text: string = cell.getValue();
            var { section, tab } = utilityHelper.getSectionError(props.tableName);

            if (section.length === 0 && tab.length === 0) {
                return text;
            }

            if ((typeof text) === "undefined") {
                return text;
            }

            // Verify if there is an error in this section
            var errorSection: ErrorSummaryAFRSection = errorList.find((x: any) => x.section.sectionCode === section);
            if ((typeof errorSection) === "undefined") {
                return text;
            }

            // Discover the array of errors for the section
            var error = errorSection.errorList.find(x => x.entityType === tab && x.entityCode === cell.getData().accountCode);

            if ((typeof error) === "undefined") {
                return text;
            } else {
                if (error !== undefined) {
                    return "<i class='bi bi-x-octagon-fill text-danger' title='" + error.description + "'></i> " + text;
                }
            }


        };


        const getCellAddress = function (cell: any) {
            var row = cell.getRow();
            if (row._row.type != "row") { return "na" }
            const rowCode = row.getData().accountCode;
            const fld = cell.getField();
            return rowCode + "|" + fld;
        }

        // special logic to verify if a cell is editable or not
        // editable    --> return true
        // no editable --> return false
        const handleEditCheck = function (cell: any) {
            if (currentAfr && currentAfr.taxonomyType) {
                switch (currentAfr.taxonomyType) {
                    case TaxonomyTypeEnum.COURT_REPORTING_29:
                    case TaxonomyTypeEnum.COURT_REPORTING_939:
                        return false;
                }
            }
            if (isDoneOrNo()) {
                return false;
            }
            if (cell.getField() === "amount") {
                return true;
            }
            const allowedList = props.options.allowedCombinations;
            if (allowedList) {
                const cellAddr: string = getCellAddress(cell);
                if (allowedList.indexOf(cellAddr) >= 0) {
                    return true;
                } else {
                    return false;
                }
            }
            return true;
        }

        const handleHeaderClick = function (e: any, column: any) {
            // get the column name to pull the concept associated to it
            var fundCode = column.getField();
            dispatch(loadConcept(fundCode,
                currentAfr ? currentAfr.taxonomyType : TaxonomyTypeEnum.AFR,
                currentAfr ? currentAfr.year : 2019));

        }
        const handleCellClick = function (e: any, cell: any) {
            //e - the click event object
            //cell - cell component
            var row = cell.getRow();
            if (row._row.type != "row") { return }

            //var table = cell.getTable();
            // Don't do the scrolling any more
            /*
            if (!props.options.fixedRows && !cell) {
                row.scrollTo();
                //table.scrollToRow(row.getData().accountCode, "center", true);
            }
            */
            var fld = cell.getField();
            if (fld === "FundGroupsAxis") {
                var dimId = row.getData().dimensionCode;
                if (dimId) {
                    dispatch(loadConcept({
                        conceptId: "FundCode_" + dimId,
                        taxonomyType: currentAfr ? currentAfr.taxonomyType : TaxonomyTypeEnum.AFR,
                        afrYear: afrYear
                    }));
                }
            } else {
                var accountId = row.getData().accountCode;
                if (accountId) {
                    dispatch(loadConcept({
                        conceptId: "AccountCode_" + accountId,
                        taxonomyType: currentAfr ? currentAfr.taxonomyType : TaxonomyTypeEnum.AFR,
                        afrYear: afrYear
                    }));
                }
            }
        }
        const handleDeleteCellClick = function (event: any, cell: any) {
            event.stopPropagation();

            if (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin) {
                return;
            }
            
            if (isDoneOrNo() === true) {
                alert("You must un-check the done flag to delete rows");
                return;
            }
            cleanup();

            var table = cell.getTable();
            if (!table) {
                return;
            }
            var row: any = cell.getRow();
            var rowData = row.getData();
            var accParts: string[] = rowData.accountCode.split("|");
            setDisplayCode(accParts[0]);
            var rowToSelect = row.getPrevRow();
            if (!rowToSelect) {
                rowToSelect = row.getNextRow();
            }
            var nextAccountCode = "";
            if (rowToSelect) {
                nextAccountCode = rowToSelect.getData().accountCode;
            }
            var rowMatch: MatchTableFacts = {
                tableName: props.tableName,
                matchConcept: {
                    conceptId: rowData.accountId,
                    dimensions: rowData.rowDimensions,
                    conceptName: nextAccountCode,
                    taxonomyType: utilityHelper.getTaxonomyTypeFromVersion(versionInfo),
                    afrYear: currentAfr !== null ? currentAfr.year : 0
                }
            }
            setDeleteRequest(rowMatch);
        }
        const getRowKey = function (cell: any) {
            var row = cell.getRow();
            var dataRow = row.getData();
            var key = "";

            if (dataRow.hasOwnProperty("dimensionFields")) {
                for (var field of dataRow.dimensionFields) {
                    key = key + (dataRow[field] ? dataRow[field] : "") + "|";
                }
            }
            return key;
        }


        // logic to handle the dirty flag in redux store
        const handleEdited = function (cell: any) {

            var newValue: string = cell.getValue();
            var rowData: any = cell.getRow().getData();
            var field: string = cell.getField();
            newValue = newValue.trim();
            var prevEffectiveValue: string = utilityHelper.getEffectiveStringValue(cell.getOldValue(), rowData, field);
            var newEffectiveValue: string = utilityHelper.getEffectiveStringValue(newValue, rowData, field);
            if ((prevEffectiveValue !== newEffectiveValue) || (newValue.length > 0 && utilityHelper.getEffectiveNumericValue(newValue) === 0)) {
                // Turn ON the dirty flag in redux
                dispatch(setIsDirty({ IsDirty: true, Page: "" }));
                var tName = props.tableName;
                if (props.options && props.options.accountRangeFilter) {
                    tName += "^" + props.options.accountRangeFilter.first + "^" + props.options.accountRangeFilter.last;
                }
                utilityHelper.updateTableCell(tName, props.options.columnFilter, cell, cell.getTable(), dispatch);
                props.setHasData(true);
            }

        }



        // calculate totals for each row
        const getTotals = function (value: any, data: any, type: any, params: any, component: any) {
            let total = 0.0;
            Object.getOwnPropertyNames(data).forEach(
                function (val, idx, array) {
                    // Fetch the value associated with the property and try to convert to a number
                    // NOTE - some of the row attributes are metadata, not fact values, so they need to be skipped
                    switch (val) {
                        case "account":
                        case "accountId":
                        case "accountCode":
                        case "Total":
                            break;
                        default:
                            var cval = utilityHelper.getFloatValue(data[val]);
                            if (cval != 0) {
                                total = total + cval;
                            }
                            break;
                    }
                }
            );
            return total;
        }

        const handleRowClick = function (e: any, row: any) {
            //e - the click event object
            //row - row component
            if (row._row.type != "row") { return }

            row.getTable().deselectRow();
            row.select();
        }

        const newGridOptions = {
            layout: "fitColumns",
            columnHeaderVertAlign: "bottom",
            layoutColumnsOnNewData: true,
            dataTree: true,
            selectable: 1,
            selectableRollingSelection: false,
            height: "630",  // to be adjusted if another thing is decided
            // columnMaxWidth: 120,
            index: "accountCode",
            cellEdited: (cell: any) => handleEdited(cell),
            cellClick: (e: any, cell: any) => handleCellClick(e, cell),
            rowClick: (e: any, cell: any) => handleRowClick(e, cell),
            ...props.gridOptions
        }

        const addAccountGroup = function (nextRow: any) {
            if (props.options.accountGroups) {
                var accountGroup = "";
                if (props.options.accountGroups.hasOwnProperty(rowConcept.code)) {
                    accountGroup = props.options.accountGroups[rowConcept.code];
                }
                nextRow["accountGroup"] = accountGroup;
            }
        }

        const isEmptyColumn = function (tableDataRows: any[], fld: string) {
            for (var dataRow of tableDataRows) {
                if (!dataRow.hasOwnProperty(fld)) {
                    continue;
                }
                var val = dataRow[fld];
                if (val == null) {
                    continue;
                }
                return false;
            }
            return true;
        }
        const collapseEmptyColumns = function (table: any) {
            var colDefs: any[] = table.getColumnDefinitions();
            var tableRows: any[] = table.getData();
            var hcm: any = {
            }
            if (colDefs) {
                colDefs.map(cd => {
                    if (cd.hasOwnProperty("columns")) {
                        var cols: any[] = cd.columns;
                        cols.map(col => {
                            if (col.hasOwnProperty("validator") && isEmptyColumn(tableRows, col.field)) {
                                if (!hiddenColumnMap.hasOwnProperty(col.field)) {
                                    // we are going to hide it
                                    hcm[col.field] = col.field;
                                }
                                table.toggleColumn(col.field);
                            }
                        });
                    }
                });
            }
            setHiddenColumnMap(hcm);
        }
        const collapseEmptyColumnsFormatter = function (cell: any, formatterParams: any, onRendered: any) {

            //build dropdown
            var button = document.createElement("div");

            button.innerHTML = "<i class='bi bi-filter-circle' title='Toggle collapse of empty columns...'></i><span> Show/Hide empty columns</span>";

            button.onclick = function () {
                collapseEmptyColumns(cell.getTable());
            }

            return button;
        }

        const plusAccountTitleFormatter = function (cell: any, formatterParams: any, onRendered: any) {

            //build dropdown
            var button = document.createElement("div");

            button.innerHTML = "<i class='bi bi-plus-circle' title='Add new Account...'></i>";

            button.onclick = function () {
                // add code here
                var swalMessage: string | null = null;
                var dataSection = utilityHelper.getDataSection(props.options.columnFilter, props.tableName);
                if (isDoneOrNo() === false) {
                    setModalAccountIsOpen(true);
                } else if (isDone()) {
                    swalMessage = "To enter " + dataSection + ", please de-select the Done box.";
                } else if (isNoData()) {
                    swalMessage = "To enter " + dataSection + ", please de-select the No " + dataSection + " box.";
                } else {
                    swalMessage = "This AFR is locked, you cannot enter " + dataSection + ".";
                }
                if (swalMessage) {
                    Swal.fire(
                        "Cannot add accounts for " + dataSection,
                        swalMessage,
                        'info'
                    );

                }
                
            }

            return button;
        }

        const isCR29 = function (currentAfr: Afr | null) {
            if (!currentAfr || !currentAfr.taxonomyType) {
                return false;
            }
            return currentAfr.taxonomyType === TaxonomyTypeEnum.COURT_REPORTING_29
        }

        const removeRowTitleFormatter = function (cell: any, formatterParams: any, onRendered: any) {

            return "<span style='color:red'><i class='bi bi-dash-circle' title='Remove row'></i></span>";
        }
        var headerMenu = [
            {
                label: "Add Account",
                action: function (e: any, column: any) {
                    // add code here
                    if (tabulatorRef.current && tabulatorRef.current.table) {
                        var table = tabulatorRef.current.table;
                        if (isDoneOrNo() === false) {
                            table.deselectRow();
                            setModalAccountIsOpen(true);
                        }
                    }
                }
            },
        ]

        const defaultColumns: any = [];

        var maxHeaderRows = 1;
        var acctTable: FactTable | null = null;
        if (currentAfr) {
            acctTable = getTable(currentAfr, props.tableName);

            if (acctTable) {
                for (var tbeGroup of acctTable.structure.nthlevelcolumnHierarchy) {
                    if (tbeGroup.sublevels) {
                        maxHeaderRows = 2;
                    }
                }
            }
        }
        if (props.options.fixedRows) {
            if (maxHeaderRows === 1 || isCR29(currentAfr)) {
                defaultColumns.push(
                    {
                        title: "Account Code",
                        field: "account",
                        width: 280,
                        frozen: true,
                        formatter: showErrorIcon
                    }
                );
                if (props.options.dimensionColumn) {
                    defaultColumns.push(
                        {
                            title: props.options.dimensionColumn.title,
                            field: props.options.dimensionColumn.axis,
                            width: 180,
                            frozen: true,
                            editable: false,
                            formatter: showErrorIcon
                        }
                    );
                }
            } else {
                if (props.options.dimensionColumn) {
                    defaultColumns.push(
                        {
                            title: "",
                            headerHozAlign: "center",
                            frozen: true,
                            columns: [
                                { title: "Account Code", field: "account", width: 280 },
                                {
                                    title: props.options.dimensionColumn.title,
                                    field: props.options.dimensionColumn.axis,
                                    width: 180,
                                    frozen: true,
                                    editable: false,
                                    formatter: showErrorIcon
                                }
                            ]
                        }
                    );
                } else {
                    defaultColumns.push(
                        {
                            title: "",
                            headerHozAlign: "center",
                            frozen: true,
                            columns: [
                                { title: "Account Code", field: "account", width: 280, formatter: showErrorIcon },
                            ]
                        }
                    );
                }
            }
        } else {
            defaultColumns.push(
                {
                    title: "",
                    headerHozAlign: "center",
                    frozen: true,
                    titleFormatter: collapseEmptyColumnsFormatter,
                    columns: [
                        { title: "Account Code", field: "account", width: 280, formatter: showErrorIcon, titleFormatter: "plaintext" },
                        { title: "Account Code", field: "action", width: 25, headerSort: false, cellClick: handleDeleteCellClick, formatter: removeRowTitleFormatter, titleFormatter: plusAccountTitleFormatter }
                        //                        { title: "Account Code", field: "account", width: 280, formatter: showErrorIcon, titleFormatter: "plaintext", headerMenu: headerMenu }
                    ]
                }
            );
        }


        var allowedColumnDimensions: any | null = null;
        if (props.options.columnFilter) {
            newGridOptions.layout = "fitDataStretch";
            allowedColumnDimensions = {};
        }
        const allowedCells = props.options.allowedCombinations;
        var dataColumns : any[] = [];
        if (acctTable) {
            // Note - it is possible for the requested account table to not be present in this AFR
            // However that should be deal with upstream so the option doe snot appear
            for (var tbeGroup of acctTable.structure.nthlevelcolumnHierarchy) {
                if (props.options.columnFilter && tbeGroup.label !== props.options.columnFilter) {
                    continue;
                }
                var t : any | null = null;
                var tGroup = "";
                var colGroup : any[] = [];
                if (tbeGroup.sublevels) {
                    for (var tbeCol of tbeGroup.sublevels) {
                        if (tbeCol.dimensionaspect != null && tbeCol.dimensionaspect.member != null) {
                            t = tbeCol.dimensionaspect.member.id;
                            if (props.options.columnFilter) {
                                // We only here if this column group has passed the filter.
                                allowedColumnDimensions[t] = tbeCol;
                            }
                        } else {
                            t = null;
                        }

                        var titletext = tbeCol.label.replace(" Funds", "").replace(" Fund", "");
                        if (allowedCells) {
                            // We don't have column sums for restricted input
                            colGroup.push({
                                title: titletext,
                                field: t,
                                headerSort: false,
                                editor: "input",
                                editable: handleEditCheck,
                                headerClick: handleHeaderClick,
                                editorParams: { mask: "*999999999999" },
                                validator: "numeric",
                                headerHozAlign: "right",
                                hozAlign: "right",
                                formatter: "money", formatterParams: { symbol: "$", precision: 0 },
                                cellEdited: handleEdited,
                                cellClick: handleCellClick,
                                mutateLink: "Total"
                            });
                        } else {
                            if (props.options.columnFilter) {
                                newGridOptions.layout = "fitDataStretch";
                            } else {
                                newGridOptions.layout = "fitData";
                            }
                            var vis: boolean = true;
                            if (hiddenColumnMap.hasOwnProperty(t)) {
                                vis = false;
                            }
                            colGroup.push({
                                title: titletext,
                                field: t,
                                headerSort: false,
                                editor: "input",
                                width: 160,
                                editable: handleEditCheck,
                                headerClick: handleHeaderClick,
                                editorParams: { mask: "*999999999999" },
                                validator: "numeric",
                                headerHozAlign: "right",
                                hozAlign: "right",
                                topCalc: "sum",
                                topCalcFormatter: "money",
                                topCalcFormatterParams: { symbol: "$", precision: 0 },
                                formatter: "money", formatterParams: { symbol: "$", precision: 0 },
                                visible: vis,
                                cellEdited: handleEdited,
                                cellClick: handleCellClick,
                                mutateLink: "Total"
                            });
                        }
                        dataColumns.push(t);
                    }
                    tGroup = tbeGroup.label;
                } else {
                    t = (tbeGroup.dimensionaspect != null && tbeGroup.dimensionaspect.member != null) ? tbeGroup.dimensionaspect.member.id : "amount";
                    if (props.options.columnFilter) {
                        // We only here if this column group has passed the filter.
                        allowedColumnDimensions[t] = tbeGroup;
                    }
                    var titletext = tbeGroup.label.replace(" Funds", "").replace(" Fund", "").replace("root", "Amount");
                    if (allowedCells) {
                        if (props.options.accountGroups) {
                            colGroup.push({
                                title: titletext,
                                field: t,
                                headerSort: false,
                                editor: "input",
                                editable: handleEditCheck,
                                headerClick: handleHeaderClick,
                                editorParams: { mask: "*999999999999" },
                                validator: "numeric",
                                headerHozAlign: "right",
                                hozAlign: "right",
                                bottomCalc: "sum",
                                bottomCalcFormatter: "money",
                                bottomCalcFormatterParams: { symbol: "$", precision: 0 },
                                formatter: "money", formatterParams: { symbol: "$", precision: 0 },
                                cellEdited: handleEdited,
                                cellClick: handleCellClick,
                                mutateLink: "Total"
                            });
                        } else {
                            colGroup.push({
                                title: titletext,
                                field: t,
                                headerSort: false,
                                editor: "input",
                                editable: handleEditCheck,
                                headerClick: handleHeaderClick,
                                editorParams: { mask: "*999999999999" },
                                validator: "numeric",
                                headerHozAlign: "right",
                                hozAlign: "right",
                                formatter: "money", formatterParams: { symbol: "$", precision: 0 },
                                cellEdited: handleEdited,
                                cellClick: handleCellClick,
                                mutateLink: "Total"
                            });
                        }
                    } else {
                        var visNoGroup: boolean = true;
                        if (hiddenColumnMap.hasOwnProperty(t)) {
                            visNoGroup = false;
                        }
                        colGroup.push({
                            title: titletext,
                            field: t,
                            headerSort: false,
                            editor: "input",
                            editable: handleEditCheck,
                            headerClick: handleHeaderClick,
                            editorParams: { mask: "*999999999999" },
                            validator: "numeric",
                            headerHozAlign: "right",
                            hozAlign: "right",
                            topCalc: "sum",
                            topCalcFormatter: "money",
                            topCalcFormatterParams: { symbol: "$", precision: 0 },
                            formatter: "money", formatterParams: { symbol: "$", precision: 0 },
                            visible: visNoGroup,
                            cellEdited: handleEdited,
                            cellClick: handleCellClick,
                            mutateLink: "Total"
                        });
                    }
                    dataColumns.push(t);
                }
                if (maxHeaderRows < 2 || isCR29(currentAfr) ) {
                    for (var col of colGroup) {
                        defaultColumns.push(col);
                    }
                } else {
                    defaultColumns.push({
                        title: tGroup,
                        field: '',
                        headerHozAlign: "center",
                        columns: colGroup
                    });
                }
            }
        }

        // Adding Total Column - if not using restricted cell model where there are no totals
        if (!allowedCells) {
            defaultColumns.push(
                {
                    title: 'Total',
                    headerHozAlign: "center",
                    field: 'Total',                           
                    headerSort: false,
                    hozAlign: 'right',
                    editor: false,
                    width: 200,
                    mutator: getTotals,
                    topCalc: "sum",
                    topCalcFormatter: "money",
                    topCalcFormatterParams: { symbol: "$", precision: 0 },
                    formatter: "money", formatterParams: { symbol: "$", precision: 0 },
                }
            );
        }


        var tabledata: any = [];
        var dimColumnSpecs = props.options.dimensionColumn;
        var dimensionColumnAxis = dimColumnSpecs ? dimColumnSpecs.axis : null;
        if (acctTable) {
            var columnAxis = "FundGroupsAxis"; // Should derive from structure
            switch (taxType) {
                case TaxonomyTypeEnum.COURT_REPORTING_29:
                    // NOTE - The column axis varies in different uses of this same component, so we have to get the value from the structure
                    if (acctTable.structure && acctTable.structure.columnHierarchy) {
                        var colAxes = acctTable.structure.columnHierarchy.axes;
                        if (colAxes && colAxes.length > 0) {
                            columnAxis = colAxes[0].id;
                        }
                    }
                    break;
            }
            if (props.options.fixedRows) {
                var rowMapping: any = {};
                var rowDimensions: Array<DimensionReference> = [];
                // If there are row dimensions then we need to extract them
                if (acctTable.structure.rowHierarchy.axes && acctTable.structure.rowHierarchy.axes.length > 0) {
                    for (var rowAxis of acctTable.structure.rowHierarchy.axes) {
                        if (dimensionColumnAxis && (rowAxis.id === dimensionColumnAxis)) {
                            // For now only process one axis - the one designated in the options
                            // can be expanded to multiple axes later.
                            for (var memConcept of rowAxis.membersByYear) {
                                rowDimensions.push({ axis: rowAxis, member: memConcept, typedContent: null });
                            }
                        }
                    }
                }
                var nextRow: any = {};
                for (var rowConcept of acctTable.structure.rows) {
                    var accountLabel = rowConcept.name;
                    if (accountLabel.indexOf("Title") >= 0) {
                        continue;
                    }
                    // Now expand the rows on the dimensions (if any)
                    if (rowDimensions.length > 0) {
                        // Only process the first level for now
                        for (var rowDim of rowDimensions) {
                            nextRow = {
                                account: accountLabel, accountCode: rowConcept.code, accountId: rowConcept.id
                            }
                            if (rowDim.member != null && dimensionColumnAxis && props.options.dimensionColumn
                                && props.options.dimensionColumn.codeLabelMap.hasOwnProperty(rowDim.member.code)) {
                                var rid = rowConcept.id + "|" + (rowDim.member != null ? rowDim.member.id : "");
                                var testid = rowConcept.code + "|" + (rowDim.member != null ? rowDim.member.id : "");
                                if (!allowedCells || (allowedCells.indexOf(testid) >= 0)) {
                                    if (rowDim.member != null) {
                                        var lbl = props.options.dimensionColumn.codeLabelMap[rowDim.member.code];
                                        nextRow[dimensionColumnAxis] = lbl;
                                        nextRow["dimensionCode"] = rowDim.member.code;
                                        addAccountGroup(nextRow);
                                        tabledata.push(nextRow);
                                        rowMapping[rid] = nextRow;
                                    }
                                }
                            }
                        }
                    } else {
                        nextRow = {
                            account: accountLabel, accountCode: rowConcept.code, accountId: rowConcept.id
                        }
                        addAccountGroup(nextRow);
                        tabledata.push(nextRow);
                        rowMapping[rowConcept.id] = nextRow;
                    }
                }
                if (dimColumnSpecs) {
                    // We have a table with a column representing a dimension on the fact. The column values will be the labels of the dimension identified by the dimension
                    // column axis. 
                    // Currently this is used by the DataElementWorksheet table.
                    // Collect all the facts and then reassign to the rows created above
                    var allFacts : any[] = [];
                    for (var row of acctTable.rows) {
                        for (var f of row.facts) {
                            allFacts.push(f);
                        }
                    }
                    for (var fTable of allFacts) {
                        var factColDim = getDimensionForFact(fTable, dimensionColumnAxis);
                        if (!factColDim) {
                            // todo throw an error
                            continue;
                        }
                        var accountId = fTable.concept.id + "|" + factColDim.id;
                        if (rowMapping.hasOwnProperty(accountId)) {
                            var nextRow = rowMapping[accountId];
                            var colcode = "amount";
                            nextRow[colcode] = fTable.value;
                            if (props.options.dimensionColumn) {
                                var memName = factColDim.code;
                                var lbl = dimColumnSpecs.codeLabelMap[memName];
                                nextRow[dimColumnSpecs.axis] = lbl;
                            }
                        }
                    }
                } else {
                    for (var row of acctTable.rows) {
                        if (row !== null && row.account !== null) {
                            var accountId = row.account.id;
                            if (rowMapping.hasOwnProperty(accountId)) {
                                var nextRow = rowMapping[accountId];
                                var dataColThreshold: number = 0;
                                if (taxType === TaxonomyTypeEnum.AFR) {
                                    dataColThreshold = 1;
                                }
                                if (dataColumns.length > dataColThreshold) {
                                    for (var fact of row.facts) {
                                        var dimensionForFact = getDimensionForFact(fact, columnAxis);
                                        if (dimensionForFact !== null) {
                                            var colcode = dimensionForFact.id;
                                            nextRow[colcode] = fact.value;
                                        }
                                    }

                                } else {
                                    for (var fact of row.facts) {
                                        var colcode = "amount";
                                        nextRow[colcode] = fact.value;
                                    }
                                }
                            }
                        }
                    }
                }
            } else {
                for (var row of acctTable.rows) {
                    if (shouldExcludeRow(row, props.options.accountRangeFilter, accountCodeFilter)) {
                        continue;
                    }
                    if (row != null && row.account != null) {
                        var accountLabel = row.account.name;

                        nextRow = {
                            account: accountLabel, accountCode: row.account.code, accountId: row != null && row.account != null ? row.account.id : ""
                        }
                        for (var fact of row.facts) {
                            var dimensionForFact = getDimensionForFact(fact, columnAxis);
                            if (dimensionForFact !== null) {
                                var colcode = dimensionForFact.id;
                                if (allowedColumnDimensions) {
                                    // We are filtering by columns, so only add a table fact to the table row if its one of the allowed dimensions.
                                    if (allowedColumnDimensions.hasOwnProperty(colcode)) {
                                        nextRow[colcode] = fact.value;
                                    }
                                } else {
                                    nextRow[colcode] = fact.value;
                                }
                            }
                        }

                        
                        if (accountCodeFilter && accountCodeFilter.accountMappings.hasOwnProperty(nextRow.accountCode)) {
                            if (accountCodeFilter.filter.indexOf(nextRow.accountCode) >= 0) {
                                //do not add the row to the tabledata, it should be filtered out
                                //previously blank rows were being added because the fact.value was not being assigned because it was not
                                //in the allowable column dimensions.
                            } else {
                                //this is likely a new row and should be added to the tabledata even if it does not have a fact.value associated with it yet
                                tabledata.push(nextRow);
                            }
                        }   
                        else if (row.facts.length === 0) {
                            // This is a new row that has not yet been assigned any fact values.
                            //It should be added to the tabledata so that it can be edited and have fact values assigned to it.
                            tabledata.push(nextRow);
                        }
                        else if (Object.keys(nextRow).length > 3) {
                            //the fact value is associated with an allowed dimension you can safely add the row to the tabledata
                            tabledata.push(nextRow);
                        }
                    }
                }
            }
        }

        useEffect(() => {
            if (props.options.columnFilter) {
                var scopeMapping: DimensionalAccountScope = getAccountFilterForFundGroup(props.options.columnFilter, scopeNetworks);
                setAccountCodeFilter(scopeMapping);
            }
        }, [props]);

        useEffect(() => {
            if (deleteRequest) {
                var dreq: MatchTableFacts = deleteRequest;
                setDeleteRequest(null);
                utilityHelper.showDeletePopup(currentAfr, dreq, displayCode, dispatch);
            }
        }, [deleteRequest]);

        useEffect(() => {
            if (parameters.state && parameters.state.entityCode && parameters.state.entityCode.length > 0) {
                setDataKey(parameters.state.entityCode);
            }
        }, [getEntityCodeFromUrl()]);

        const cleanup = function() {
            utilityHelper.saveDeferredChanges(dispatch);
        }

        React.useEffect(() => {

            var filteredRows: any[] = [];

            if (currentAfr && currentAfr.year > 2023) {

                let columnFilterValue = props.options.columnFilter ? props.options.columnFilter : null;

                let fundGroupMemberIds: Set<string> = utilityHelper.getFundGroupMemberIdSetFromAfr(currentAfr, columnFilterValue, props.tableName);

                //Filter on either you have an assigned fund group from being saved or you are a newly added row that exists in the account table but has no facts assigned to it.
                filteredRows = tabledata.filter((row: any) => row && ((Object.keys(row).length > 3 && Object.keys(row).some((key: string) => fundGroupMemberIds.has(key)))
                    || (columnFilterAccountsWithoutFactsSelector && columnFilterAccountsWithoutFactsSelector.some((accountFilterPair: { accountCode: string; columnFilter: string }) => accountFilterPair.accountCode == row.accountCode
                        && accountFilterPair.columnFilter == props.options.columnFilter
                    ))));

                //Lastly remove duplicate rows.  This is a temporary fix in the essence of time to fix duplicate rows appearing.
                filteredRows = filteredRows.filter((row: any, index: number, self: any[]) =>
                    index === self.findIndex((r: any) => r.accountCode === row.accountCode)
                );

            } else {
                //if the AFR is 2023 or earlier, we don't need to filter the rows because there are no fund groups to filter on.
                filteredRows = tabledata;
            }


            switch (props.options.firstFieldId) {
                case "defaultHeight":
                    tabulatorRef.current = new Tabulator("#" + props.options.gridname, {
                        data: filteredRows,
                        columns: defaultColumns,
                        layout: "fitDataStretch",
                        layoutColumnsOnNewData: true,
                        selectable: 1,
                        selectableRollingSelection: false,
                        scrollToRowPosition: "center",
                        index: "accountCode",

                    }).on("rowClick", handleRowClick);
                    break;

                default:

                    tabulatorRef.current = new Tabulator("#" + props.options.gridname, {
                        data: filteredRows,
                        columns: defaultColumns,
                        layout: props.options.columnFilter ? "fitDataStretch" : "fitData",
                        layoutColumnsOnNewData: true,
                        selectable: 1,
                        selectableRollingSelection: false,
                        scrollToRowPosition: "center",
                        height: "630",  // to be adjusted if another thing is decided
                        index: "accountCode",

                    }).on("rowClick", handleRowClick);
                    break;
            }

            return () => {
                cleanup();
            }

        }, [currentAfr]);



        return (

            <React.Fragment>

                <div id={props.options.gridname} ref={ele => { tabulatorRef.current = ele }} />

                {acctTable && (<SelectNewAccountModal
                    tableName={props.tableName}
                    isOpen={modalAccountIsOpen}
                    onCloseModal={handleCloseAccountModal}
                    filter={props.options.accountRangeFilter}
                    scopeMappings={accountCodeFilter}
                    columnFilter={ props.options.columnFilter ? props.options.columnFilter : null}
                />)}



            </React.Fragment>
        );
    }
);

export { AccountEditorGridNoSubAccountBulk };
